import { UIElement } from '../ui-element';
import { eventBus } from '../../global/event-bus';
import { debounce } from '../../global/helpers';
import {
    mainMenuOverlayHandler,
    modalOverlayHandler,
    handleMenuOnResize,
    handleTooltipsOnResize,
    OverlayWatchEvents,
    type MenuOverlayCallbackArgs,
    type ModalOverlayCallbackArgs,
    type ResizeCallbackArgs,
} from '../../global/overlay';
import { MessageType, MessageTypeValue } from './message.const';
import { browser } from '../../global/browser';
import { getDebugMode } from '../../global/config';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { Debounce, NullOr } from '../../global/utils.type';
import type { UINav } from '../menu/ui-nav';
import type { UIModalController } from '../modal/ui-modal-controller';
import type { UIModal } from '../modal/ui-modal';
import type {
    IframeMessage,
    LayoutPayload,
    OverlayPayload,
} from './ui-iframe-controller.type';

/**
 * @class
 * @memberof SharedComponents
 * @element ui-iframe-controller
 * @alias UIIFrameController
 * @augments {UIElement}
 * @classdesc Represents a class for <code>ui-iframe-controller</code> element.
 * Watches the resizing of the iframe embedded viewport and listen messages.
 * @property {string} rootSelector {@attr root-selector}
 * @property {string} [mainMenuSelector="#nav-main"] {@attr main-menu-selector}
 * @property {string} [targetOrigin='*'] {@attr target-origin} Origin which should receive the message, by default it '*' any origin.
 * @property {boolean} menuOpened Is menu opened.
 * @property {boolean} popoverOpened Is popover opened.
 * @property {string} [allowedOrigin] {@attr allowed-origin} If set, only messages from this origin are accepted.
 * @property {HTMLElement | null} root Parent element where all content is placed.
 * @example
 * <ui-iframe-controller
 *   root-selector=".any-wrapper"
 *   target-origin="https://example.org"
 * ></ui-iframe-controller>
 */
@Component({ tag: 'ui-iframe-controller' })
export class UIIFrameController extends UIElement {
    @Attribute(String) targetOrigin: string = '*';
    @Attribute(String) rootSelector!: string;
    @Attribute(String) mainMenuSelector: string = '#nav-main';
    @Attribute(String) allowedOrigin?: string;

    menuOpened = false;
    popoverOpened = false;
    modalController!: UIModalController;

    private _currentHeight?: number;
    private _currentContentHeight: string;
    private _currentTop: string;
    private _currentTopOffset: string;
    private _currentBottom: string;
    private popoverHandler!: EventListener;
    private resizeHandler!: Debounce;
    private resizeObserver!: ResizeObserver;
    private messageHandler!: (event: MessageEvent) => void;

    constructor() {
        super();

        const compStyles = window.getComputedStyle(document.documentElement);
        this._currentContentHeight = compStyles.getPropertyValue(
            '--ui-content-height'
        );
        this._currentTop = String(window.scrollY);
        this._currentTopOffset = compStyles.getPropertyValue(
            '--ui-viewport-top-offset'
        );
        this._currentBottom = compStyles.getPropertyValue(
            '--ui-content-bottom'
        );
    }

    /**
     * Magic number for delay, usually 3 ideal frames (at 60 fps) is enough to reflow.
     * But on slower CPUs this might still cause reflow delay.
     * @type {number}
     */
    static readonly DebounceTime = 16.6667 * 3;

    /**
     * Magic offset for non ui-modal popover to add extra space for height to cover
     * extra difference like filter shadows.
     * @type {number}
     */
    static readonly PopoverOffset = 10;

    /**
     * @type {Array<string>}
     */
    static readonly NavContainers = [
        '.ui-nav__items',
        '.ui-nav__columns.-primary.-on',
        '.ui-nav__columns.-secondary.-on',
        '.ui-nav__columns.-auxiliary.-on',
        '[class*="__popover"]',
    ];

    get root(): NullOr<HTMLElement> {
        return document.querySelector<HTMLElement>(this.rootSelector);
    }

    /**
     * @param {CustomEvent | Event} event
     * @private
     */
    private openPopoverHandler(event: CustomEvent | Event): void {
        this.handleOverlay(event);
        this.handleHeight(event);
    }

    /**
     * @type {UINav | null}
     */
    get mainMenu(): NullOr<UINav> {
        return document.querySelector<UINav>(this.mainMenuSelector);
    }

    /**
     * Handles different cases when overlay should be shown.
     * @param {CustomEvent | Event} event
     */
    private handleOverlay(event: CustomEvent | Event): void {
        // 1. Is menu is opened / closed
        const openCallback = (
            args: MenuOverlayCallbackArgs<UIIFrameController>
        ): void => {
            args.controller.sendOverlayMessage(MessageType.OverlayOpened, {
                animate: args.isDesktopMenu,
                blockScroll: !args.isDesktopMenu,
                closable: true,
                partial: true,
            });
            args.controller.menuOpened = true;
        };
        const closeCallback = (
            args: MenuOverlayCallbackArgs<UIIFrameController>
        ): void => {
            args.controller.sendOverlayMessage(MessageType.OverlayClosed, {
                animate: args.isDesktopMenu,
                blockScroll: false,
                closable: true,
                partial: true,
            });
            args.controller.menuOpened = false;
        };
        mainMenuOverlayHandler(this, event, openCallback, closeCallback);

        // 2. Modals are opened as fullscreen and popovers.
        const enterCallback = (
            args: ModalOverlayCallbackArgs<UIIFrameController>
        ): void => {
            const target = event.target as NullOr<UIModal>;
            args.controller.sendOverlayMessage(MessageType.OverlayOpened, {
                animate: true,
                blockScroll: args.isNotBubble,
                closable: !!target?.closable,
                partial: false,
            });
        };
        const exitCallback = (
            args: ModalOverlayCallbackArgs<UIIFrameController>
        ): void => {
            if (!args.hasOverlay) {
                args.controller.sendOverlayMessage(MessageType.OverlayClosed, {
                    animate: false,
                    blockScroll: args.blockScroll,
                    closable: true,
                    partial: args.isNavToggle,
                });
                args.controller.popoverOpened = false;
            }
        };
        const updateCallback = (
            args: ModalOverlayCallbackArgs<UIIFrameController>
        ): void => {
            const detail: { action?: string } =
                event instanceof CustomEvent && event.detail
                    ? (event.detail as { action?: string })
                    : { action: '' };
            const type =
                detail.action === 'add'
                    ? MessageType.OverlayOpened
                    : MessageType.OverlayClosed;
            args.controller.sendOverlayMessage(type, {
                animate: false,
                blockScroll: args.blockScroll,
                closable: true,
                partial: args.isNavToggle,
            });
        };
        void modalOverlayHandler(
            this,
            event,
            enterCallback,
            exitCallback,
            updateCallback
        );
    }

    /**
     * @param {string} [type]
     * @param {OverlayPayload} payload
     * @private
     */
    private sendOverlayMessage(
        type: MessageTypeValue = MessageType.OverlayClosed,
        payload: OverlayPayload
    ): void {
        this.postMessage({ type, payload });
    }

    /**
     * Updates current height.
     * @param {CustomEvent | Event} [event]
     * @param {object} [data]
     */
    private handleHeight(
        event?: NullOr<CustomEvent | Event>,
        data?: object
    ): void {
        const target = event?.target;
        const isHTMLElement = target instanceof HTMLElement;
        const isFixedLayout =
            !browser.desktopMediaMatches() &&
            !!isHTMLElement &&
            !!target.closest(
                'ui-modal.-position-fixed, ui-modal.-navtoggle, ui-navtoggle, ui-nav'
            );
        if (event && isFixedLayout) {
            return;
        }

        const height = this.getHighest();
        const toChange = this._currentHeight !== height || data;

        if (toChange) {
            this._currentHeight = height;
            this.setIframeHeight(this._currentHeight);
        }
    }

    /**
     * @returns {number}
     */
    private getHighest(): number {
        const rootElement = this.root;
        if (!rootElement) {
            return 0;
        }

        const elems = [rootElement, ...this.getAllPopovers()].filter(
            (element): element is HTMLElement => element !== null
        );
        const rootHeight = rootElement.getBoundingClientRect().height;
        return elems.reduce((prev, next) => {
            const bs = next.getBoundingClientRect();
            let h = bs.height + bs.top;
            if (!['ui-modal', 'ui-tooltip'].includes(next.localName)) {
                h += UIIFrameController.PopoverOffset;
            }
            return Math.max(prev, h, rootHeight);
        }, 0);
    }

    /**
     * Handles when outside click from postMessages received
     * @private
     */
    private onClickReceived(): void {
        const isDesktop = browser.desktopMediaMatches();

        // 1. Menu
        if (isDesktop) {
            this.mainMenu?.close();
        } else {
            eventBus.dispatchCustomEvent('menu-toggle', { active: false });
        }

        // 2. Nav-toggles
        if (!isDesktop) {
            eventBus.dispatchCustomEvent('navtoggle-closed');
        }

        // 3 .Modals
        const modal = this.modalController.getActiveTopModalWindow();
        modal?.close();

        // 4. All other cases
        document.dispatchEvent(new PointerEvent('click'));
    }

    /**
     * @param {number} height
     */
    setIframeHeight(height: number): void {
        this.postMessage({
            type: MessageType.HeightChanged,
            payload: height,
        });
    }

    /**
     * @param {IframeMessage} message
     * @private
     */
    private postMessage(message: IframeMessage): void {
        window.parent.postMessage(message, this.targetOrigin);
    }

    /**
     * @returns {Array<HTMLElement>}
     */
    private getAllPopovers(): Array<HTMLElement> {
        let elems: Array<HTMLElement> = [];
        if (this.modalController) {
            elems = elems.concat([...this.modalController.getActiveModals()]);
        }
        const navs = document.querySelectorAll<HTMLElement>(
            UIIFrameController.NavContainers.join(',')
        );
        elems = elems.concat([...navs]);
        return elems;
    }

    /**
     * @param {MessageEvent} event
     */
    onMessageHandler(event: MessageEvent): void {
        const data = event.data as IframeMessage<LayoutPayload | object>;
        // There always should be a type.
        if (!data?.type) {
            return;
        }

        if (this.allowedOrigin && event.origin !== this.allowedOrigin) {
            console.error(
                `${event.origin} is forbidden, check allowed-origin attribute`
            );
            return;
        }

        switch (data.type) {
            case MessageType.LayoutUpdate:
                this.handleHeight(null, data);
                break;
            case MessageType.PopoverUpdate:
                this.onPopoverUpdate(data.payload as LayoutPayload);
                break;
            case MessageType.Click:
                this.onClickReceived();
                break;
            default:
                break;
        }
    }

    /**
     * Handles layout update
     * @param {LayoutPayload} payload
     * @private
     */
    private onPopoverUpdate(payload?: LayoutPayload): void {
        if (!payload) {
            return;
        }

        const top = payload.top + 'px';
        const topOffset = payload.topOffset + 'px';
        const contentHeight = payload.height + 'px';
        const bottom = payload.bottom + 'px';

        if (this._currentTop !== top) {
            this._currentTop = top;
            document.documentElement.style.setProperty(
                '--ui-viewport-top',
                top
            );
        }

        if (this._currentTopOffset !== topOffset) {
            this._currentTopOffset = topOffset;
            document.documentElement.style.setProperty(
                '--ui-viewport-top-offset',
                topOffset
            );
        }

        if (this._currentContentHeight !== contentHeight) {
            this._currentContentHeight = contentHeight;
            document.documentElement.style.setProperty(
                '--ui-content-height',
                contentHeight
            );
        }

        if (this._currentBottom !== bottom) {
            this._currentBottom = bottom;
            document.documentElement.style.setProperty(
                '--ui-content-bottom',
                bottom
            );
        }
    }

    /**
     * @param {Array<ResizeObserverEntry>} entries
     * @private
     */
    private observeResizeHandler(entries: Array<ResizeObserverEntry>): void {
        for (const entry of entries) {
            if (entry.contentRect) {
                this.resizeHandler();
            }
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.modalController = document.querySelector<UIModalController>(
            'ui-modal-controller'
        )!;
        if (!this.modalController) {
            console.warn(
                `${this.constructor.name}: ui-modal-controller not found`
            );
        }
        if (!this.root) {
            getDebugMode() &&
                console.error(
                    `${this.constructor.name}: root element ${this.rootSelector} doesn't exist!`
                );
            return;
        }
        // For some cases like height it might require debounce() GUI-2466.
        this.setIframeHeight(this.getHighest());
        this.popoverHandler = this.openPopoverHandler.bind(this);
        this.resizeHandler = debounce(
            this.handleHeight.bind(this),
            UIIFrameController.DebounceTime
        );
        this.resizeObserver = new ResizeObserver((entries) =>
            this.observeResizeHandler(entries)
        );
        this.resizeObserver.observe(this.root);
        OverlayWatchEvents.forEach((name) => {
            eventBus.addEventListener(name, this.popoverHandler);
        });
        this.mainMenu?.mobileMediaQueryList.addListener((event) => {
            const handleMenuOnResizeCallback = (
                args: ResizeCallbackArgs<UIIFrameController>
            ): void => {
                const type = args.isMobileMediaMatches
                    ? MessageType.OverlayOpened
                    : MessageType.OverlayClosed;
                const tempFn = (messageType: MessageTypeValue): void => {
                    args.controller.sendOverlayMessage(messageType, {
                        animate: !args.isMobileMediaMatches,
                        blockScroll: args.isMobileMediaMatches,
                        closable: true,
                        partial: true,
                    });
                };
                if (args.controller.popoverOpened) {
                    tempFn(type);
                    return;
                }
                if (args.controller.menuOpened) {
                    args.controller.menuOpened = false;
                    args.controller.popoverOpened = false;
                    tempFn(MessageType.OverlayClosed);
                }
            };
            handleMenuOnResize(this, event, handleMenuOnResizeCallback);
        });
        browser.mobileMedia.addListener((event) => {
            const handleTooltipOnResizeCallback = (
                args: ResizeCallbackArgs<UIIFrameController>
            ): void => {
                const type = args.isMobileMediaMatches
                    ? MessageType.OverlayOpened
                    : MessageType.OverlayClosed;
                args.controller.sendOverlayMessage(type, {
                    animate: false,
                    blockScroll: args.isMobileMediaMatches,
                    closable: true,
                    partial: false,
                });
            };
            handleTooltipsOnResize(this, event, handleTooltipOnResizeCallback);
        });

        this.messageHandler = this.onMessageHandler.bind(this);
        window.addEventListener('message', this.messageHandler);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        window.removeEventListener('message', this.messageHandler);
        this.resizeObserver?.disconnect();
        OverlayWatchEvents.forEach((name) => {
            eventBus.removeEventListener(name, this.popoverHandler);
        });
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        window.addEventListener('message', this.messageHandler);
        if (this.root) {
            this.resizeObserver?.observe(this.root);
        }
        OverlayWatchEvents.forEach((name) => {
            eventBus.addEventListener(name, this.popoverHandler);
        });
    }
}
